import { useMemo } from 'react'
import { buttonVariants } from '@cocoonspace/ui/components/button'
import { Card } from '@cocoonspace/ui/components/card'
import {
	Carousel,
	CarouselContent,
	CarouselItem,
	CarouselNext,
	CarouselPrevious,
} from '@cocoonspace/ui/components/carousel'
import { useIsMobile } from '@cocoonspace/ui/hooks/use-mobile'
import { cn } from '@cocoonspace/ui/lib/utils'
import { Trans, useLingui } from '@lingui/react/macro'
import { ArrowRightIcon } from 'lucide-react'
import Image from 'next/image'
import Link from 'next/link'

import { Rating } from '~/components/site-footer/baseline-and-rating'
import { SectionTitle } from '~/domains/home/components/section-title'
import { SectionWrapper } from '~/domains/home/components/section-wrapper'

const ReviewCard = ({
	index,
	review,
}: {
	index: number
	review: {
		author: string
		body: string
		cocoon: string
		context: string
		jobTitle: string
	}
}) => {
	return (
		<Card className='flex min-h-[340px] flex-col gap-4 border p-6 text-left sm:max-w-[280px]'>
			<header className='flex flex-row items-center gap-4'>
				<Image
					alt={review.author}
					className='rounded-full'
					height={50}
					src={`/images/home/user-${index + 1}.png`}
					width={50}
				/>

				<div>
					<p className='text-sm font-bold'>{review.author}</p>
					<p className='text-sm'>{review.jobTitle}</p>
				</div>
			</header>

			<div className='flex flex-col gap-8'>
				<p className='text-sm'>{review.body}</p>
			</div>

			<footer className='mt-auto text-right'>
				<p className='text-primary text-sm font-bold'>{review.context}</p>
				<p className='text-sm'>{review.cocoon}</p>
			</footer>
		</Card>
	)
}

export const HomeTestimonials = ({ className }: { className?: string }) => {
	const { t } = useLingui()
	const isMobile = useIsMobile()

	const reviewText = useMemo(
		() => [
			{
				author: 'Belkacem',
				body: t`Je recommande vivement Cocoon Space. Des lieux réfléchis pour nous permettre de nous concentrer sur l'essentiel !`,
				cocoon: t`au Cocoon Marcel`,
				context: t`Séance de coaching`,
				jobTitle: t`Coach de dirigeant`,
			},
			{
				author: 'Sylvain',
				body: t`Notre équipe étant en télétravail permanent, ce Cocoon est devenu notre RDV habituel pour nos réunions d'équipe tous les mois. Toujours très satisfait du service et de la salle !`,
				cocoon: t`au Cocoon Jean`,
				context: t`Comité récurrent`,
				jobTitle: t`CTO/Co-fondateur`,
			},
			{
				author: 'Yannick',
				body: t`Plusieurs fois par mois, nous réunissons nos équipes qui sont en télétravail dans un Cocoon : cela nous permet de nous retrouver et d’échanger, avec à chaque fois une salle parfaitement adaptée à nos besoins. Merci !`,
				cocoon: t`au Cocoon Baratier`,
				context: t`Offsite`,
				jobTitle: t`COO/Co-fondateur`,
			},
			{
				author: 'Jocelyne',
				body: t`Pour mes présentations client, j’ai trouvé le Cocoon idéal. Locaux premium, au centre de Paris, tout équipé. Le cadre est très confortable, parfait pour satisfaire aux exigences de mes clients grand compte :)`,
				cocoon: t`au Cocoon Emile`,
				context: t`Présentation client`,
				jobTitle: t`CS director`,
			},
			{
				author: 'Cyril',
				body: t`Super expérience ! Le lieu est très lumineux, bien équipé et agréable. Les accès sont très simples et les indications très claires. Bref, je recommande !!`,
				cocoon: t`au Cocoon Prisca`,
				context: t`Workshop Branding`,
				jobTitle: t`Freelance stratégie branding`,
			},
			{
				author: 'Raphael',
				body: t`Tout est simple....la résa, possession des lieux...connexion wifi...tt est à dispo pour reunion efficace et lieu très sympa.`,
				cocoon: t`au Cocoon Thomy`,
				context: t`Réunion confidentielle`,
				jobTitle: t`Directeur général`,
			},
			{
				author: 'Abdelhalim',
				body: t`J'apprécie Cocoon pour son sérieux, ses espaces réservés et bien tenus, la mise à disposition de boissons et la propriété des lieux. Le suivi par mail pour le rappel des réservations est aussi un atout. Je recommande vivement.`,
				cocoon: t`au Cocoon Louis`,
				context: t`Réunion confidentielle`,
				jobTitle: t`Avocat`,
			},
			{
				author: 'Sophie',
				body: t`Cocoon, c'est comme à la maison. La déco tendance et personnalisée rend chaque pièce unique. Les espaces sont vastes, lumineux, avec de grandes fenêtres et vues sur rue, créant une sensation d'ouverture très agréable. Je recommande à 100%.`,
				cocoon: t`au Cocoon Goetz`,
				context: t`Coaching de groupe`,
				jobTitle: t`Coach`,
			},
		],
		[t],
	)

	return (
		<div className={cn('bg-white', className)}>
			<SectionWrapper>
				<SectionTitle>
					<Trans>
						Plus de <span>30 000 clients</span> <br /> nous ont fait confiance
						pour leurs réunions clefs
					</Trans>
				</SectionTitle>

				<Carousel
					className='w-full max-md:px-8'
					opts={{
						align: 'start',
						loop: true,
						slidesToScroll: isMobile ? 1 : 3,
					}}
				>
					<CarouselContent className='-ml-6'>
						{reviewText.map((review, i) => (
							<CarouselItem
								className='flex h-full pl-6 md:basis-1/4'
								key={i}
							>
								<ReviewCard
									index={i}
									review={review}
								/>
							</CarouselItem>
						))}
					</CarouselContent>

					<CarouselPrevious className='max-md:-left-3' />
					<CarouselNext className='max-md:-right-3' />
				</Carousel>

				<div className='flex items-center justify-between gap-4'>
					<div className='flex flex-row items-center gap-2'>
						<p className='text-sm'>4,8</p>
						<Rating defaultValue={4.8} />
						<p className='text-muted-foreground text-sm'>
							sur Google (+500 avis)
						</p>
					</div>

					<Link
						className={buttonVariants({
							size: 'sm',
							variant: 'textPrimary',
						})}
						href='https://www.google.fr/maps/search/cocoon+space+salle+de+r%C3%A9union'
						rel='noreferrer'
						target='_blank'
					>
						{t`Voir tous les avis`} <ArrowRightIcon className='ml-1' />
					</Link>
				</div>
			</SectionWrapper>
		</div>
	)
}
